import axios from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { UseAIResult } from './agent-client.class';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  ChatDocument,
  DocumentContentResponse,
  DocumentPreviewResponse,
  DocumentRevision,
  FloraCapabilitiesResponse,
  FloraMemoriesResponse,
  FloraUserPreferences,
  MemoryType,
  FloraUserPreferencesResponse,
  SlashCommand,
  SlashCommandWrite,
  SuggestedPromptsResponse,
  UploadedChatDocument,
  UseAIResponse,
} from './agent-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/';

/**
 * Checks whether or not a user can use AI per configuration
 *
 * @param {string} organizationId - The ID of the organization to check.
 * @returns {UseAIResult} UseAIResult(data)
 */
function canUseAI(organizationId: string): Promise<UseAIResult> {
  const apiURL = baseURL + 'agents/use-ai/';
  return axiosInstance
    .get<UseAIResponse>(apiURL, { params: { organization_id: organizationId } })
    .then((response) => new UseAIResult(response.data))
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to check AI availability';
      console.error('[Flora] canUseAI API error:', errorMessage, error.response?.data);
      throw new Error(errorMessage);
    });
}

/**
 * Gets suggested prompts for the agent
 *
 * @param {string} organizationId - the organization id
 * @param {string | null} portfolioId - the portfolio id
 * @returns {Promise<SuggestedPromptsResponse>} list of suggested prompts
 */
function getSuggestedPrompts(organizationId: string, portfolioId: string | null): Promise<SuggestedPromptsResponse> {
  const apiURL = baseURL + 'agents/suggested-prompts/';
  return axiosInstance
    .get<string[]>(apiURL, { params: { organization_id: organizationId, portfolio_id: portfolioId } })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response.data;
      return errorMessage;
    });
}

/**
 * Gets a single document with presigned download URL
 * @param {string} documentId - The document ID
 * @returns {Promise<ChatDocument | null>} The document with download URL, or
 *   null when the id is missing or the request fails
 */
function getChatDocument(documentId: string): Promise<ChatDocument | null> {
  // An absent id would otherwise interpolate as the literal `undefined` and hit
  // the backend as a malformed UUID, which surfaces as a 500 plus a Sentry event
  // rather than a 404. Callers pass ids off already-loaded documents, so this is
  // a guard against a serializer omitting the field, not an expected path.
  if (!documentId) return Promise.resolve(null);
  const apiURL = `${baseURL}documents/${documentId}/`;
  return axiosInstance
    .get<ChatDocument>(apiURL)
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching chat document:', error.response?.data);
      return null;
    });
}

/**
 * Gets the PDF-preview status (and a fresh presigned url when ready) for a
 * Flora-generated Word/Excel document. A `processing` status means a background
 * conversion was enqueued and the caller should poll again shortly.
 *
 * @param {string} documentId - The document ID
 * @param {number} [revision] - A specific revision to preview; defaults to the current revision
 * @returns {Promise<DocumentPreviewResponse>} The preview status and, when ready, a presigned PDF url
 */
function getDocumentPreview(documentId: string, revision?: number): Promise<DocumentPreviewResponse> {
  const apiURL = `${baseURL}documents/${documentId}/preview/`;
  const params = revision != null ? { revision } : undefined;
  return axiosInstance
    .get<DocumentPreviewResponse>(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load document preview';
      console.error('Error fetching document preview:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Gets the text of a CSV, TSV, Markdown, JSON, or plain-text document revision
 * so the app can render it itself (never as HTML or in a frame).
 *
 * @param {string} documentId - The document ID
 * @param {number} [revision] - A specific revision to read; defaults to the current revision
 * @returns {Promise<DocumentContentResponse>} The revision's text and whether it was truncated
 * @throws {Error} When the request fails, so the query surfaces an error state
 */
function getDocumentContent(documentId: string, revision?: number): Promise<DocumentContentResponse> {
  const apiURL = `${baseURL}documents/${documentId}/content/`;
  const params = revision != null ? { revision } : undefined;
  return axiosInstance
    .get<DocumentContentResponse>(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load document content';
      console.error('Error fetching document content:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Lists a document's revision history (newest first), each with a short-lived
 * presigned download url for that specific revision's file.
 *
 * @param {string} documentId - The document ID
 * @returns {Promise<DocumentRevision[]>} The document's revisions, or [] on failure
 */
function getDocumentRevisions(documentId: string): Promise<DocumentRevision[]> {
  const apiURL = `${baseURL}documents/${documentId}/revisions/`;
  return axiosInstance
    .get<DocumentRevision[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => {
      // Re-throw so the query surfaces an error state: the endpoint always
      // returns at least the current revision, so a failure must not be
      // masked as an empty history.
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load document revisions';
      console.error('Error fetching document revisions:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Uploads a read-only file (Word, Excel, PDF, CSV, TSV, text, Markdown, or
 * JSON) into a chat so Flora can read it.
 *
 * The upload endpoint materializes the ChatHistory if it does not yet exist,
 * so a client-minted `chatHistoryId` works for the first message of a brand-new
 * chat (mirrors the WebSocket consumer's get-or-create).
 *
 * @param {File} file - The file to upload (see ACCEPTED_ATTACHMENT_EXTENSIONS)
 * @param {string} chatHistoryId - The chat/thread id to attach the document to
 * @param {string} organizationId - The organization ID
 * @param {AbortSignal} [signal] - Aborts the in-flight upload; the rejection is
 *   then an abort error (see {@link isAbortError}) rather than a real failure
 * @returns {Promise<UploadedChatDocument>} The stored document metadata
 * @throws {Error} On upload failure, or the original abort error when cancelled
 */
function uploadChatDocument(
  file: File,
  chatHistoryId: string,
  organizationId: string,
  signal?: AbortSignal,
): Promise<UploadedChatDocument> {
  const apiURL = `${baseURL}documents/upload/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('chat_history_id', chatHistoryId);
  formData.append('organization_id', organizationId);
  // The shared axios instance defaults to `application/json`; override it so
  // axios detects the FormData body and sends `multipart/form-data` with a
  // boundary (the upload endpoint only accepts multipart/form data).
  return axiosInstance
    .post<UploadedChatDocument>(apiURL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
    })
    .then((response) => response.data)
    .catch((error) => {
      // Rethrow a cancellation untouched so callers can tell "the user removed
      // this chip" apart from "the upload failed" — wrapping it in a new Error
      // would strip the marker axios uses to identify it.
      if (axios.isCancel(error)) throw error;
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload document';
      console.error('Error uploading chat document:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Whether a rejection came from an aborted request rather than a real failure,
 * so callers can drop it silently instead of surfacing an error to the user.
 *
 * @param {unknown} error - The rejection to classify
 * @returns {boolean} True when the request was cancelled via its `AbortSignal`
 */
function isAbortError(error: unknown): boolean {
  return axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError');
}

/**
 * Gets all documents for an organization
 * @param {string} organizationId - The organization ID
 * @returns {Promise<ChatDocument[]>} List of documents
 */
function getOrganizationDocuments(organizationId: string): Promise<ChatDocument[]> {
  const apiURL = `${baseURL}documents/`;
  return axiosInstance
    .get<ChatDocument[]>(apiURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching organization documents:', error.response?.data);
      return [];
    });
}

/**
 * Gets Flora user preferences for the current user in an organization
 * @param {string} organizationId - The organization ID
 * @returns {Promise<FloraUserPreferencesResponse>} The user preferences
 */
function getFloraUserPreferences(organizationId: string): Promise<FloraUserPreferencesResponse> {
  const apiURL = `${baseURL}flora-user-preferences/`;
  return axiosInstance
    .get<FloraUserPreferencesResponse>(apiURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch flora user preferences';
      console.error('Error fetching flora user preferences:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Updates Flora user preferences for the current user in an organization
 * @param {string} organizationId - The organization ID
 * @param {FloraUserPreferences} data - The preferences to update
 * @returns {Promise<FloraUserPreferencesResponse>} The updated preferences
 */
function updateFloraUserPreferences(
  organizationId: string,
  data: FloraUserPreferences,
): Promise<FloraUserPreferencesResponse> {
  const apiURL = `${baseURL}flora-user-preferences/update/?organization_id=${organizationId}`;
  return axiosInstance
    .put<FloraUserPreferencesResponse>(apiURL, data)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update flora user preferences';
      console.error('Error updating flora user preferences:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Gets the Flora capability catalog available to the current user in an
 * organization, already merged with the user's saved Customize Flora settings.
 *
 * @param {string} organizationId - The organization ID
 * @returns {Promise<FloraCapabilitiesResponse>} Available capability sections + tool-loading mode
 */
function getFloraCapabilities(organizationId: string): Promise<FloraCapabilitiesResponse> {
  const apiURL = `${baseURL}flora-capabilities/`;
  return axiosInstance
    .get<FloraCapabilitiesResponse>(apiURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch flora capabilities';
      console.error('Error fetching flora capabilities:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Lists the current user's saved long-term memories in an organization
 * @param {string} organizationId - The organization ID
 * @param {MemoryType} [memoryType] - Optional type filter (workflow/fact)
 * @returns {Promise<FloraMemoriesResponse>} The user's active memories
 */
function getFloraMemories(organizationId: string, memoryType?: MemoryType): Promise<FloraMemoriesResponse> {
  const apiURL = `${baseURL}flora-memories/`;
  const params: Record<string, string> = { organization_id: organizationId };
  if (memoryType) params.memory_type = memoryType;
  return axiosInstance
    .get<FloraMemoriesResponse>(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch flora memories';
      console.error('Error fetching flora memories:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Deletes a long-term memory
 * @param {string} organizationId - The organization ID
 * @param {string} id - The memory ID
 */
function deleteFloraMemory(organizationId: string, id: string): Promise<void> {
  const apiURL = `${baseURL}flora-memories/delete/?organization_id=${organizationId}&id=${id}`;
  return axiosInstance
    .delete(apiURL)
    .then(() => undefined)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete flora memory';
      console.error('Error deleting flora memory:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Gets all visible slash commands for the given organization
 * @param {string} organizationId - The organization ID
 * @returns {Promise<SlashCommand[]>} List of slash commands (global + org + user)
 */
function getSlashCommands(organizationId: string): Promise<SlashCommand[]> {
  const apiURL = `${baseURL}slash-commands/`;
  return axiosInstance
    .get<SlashCommand[]>(apiURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching slash commands:', error.response?.data);
      return [];
    });
}

/**
 * Creates a new slash command
 * @param {SlashCommandWrite} data - The command data
 * @returns {Promise<SlashCommand>} The created command
 */
function createSlashCommand(data: SlashCommandWrite): Promise<SlashCommand> {
  const apiURL = `${baseURL}slash-commands/`;
  return axiosInstance
    .post<SlashCommand>(apiURL, data)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create slash command';
      console.error('Error creating slash command:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Updates an existing slash command
 * @param {string} id - The command ID
 * @param {Partial<SlashCommandWrite>} data - The fields to update
 * @returns {Promise<SlashCommand>} The updated command
 */
function updateSlashCommand(id: string, data: Partial<SlashCommandWrite>): Promise<SlashCommand> {
  const apiURL = `${baseURL}slash-commands/${id}/`;
  return axiosInstance
    .patch<SlashCommand>(apiURL, data)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update slash command';
      console.error('Error updating slash command:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Deletes a slash command
 *
 * @param {string} id - The command ID
 */
function deleteSlashCommand(id: string): Promise<void> {
  const apiURL = `${baseURL}slash-commands/${id}/`;
  return axiosInstance
    .delete(apiURL)
    .then(() => undefined)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete slash command';
      console.error('Error deleting slash command:', errorMessage);
      throw new Error(errorMessage);
    });
}

export {
  canUseAI,
  createSlashCommand,
  deleteFloraMemory,
  deleteSlashCommand,
  getChatDocument,
  getDocumentContent,
  getDocumentPreview,
  getDocumentRevisions,
  getFloraCapabilities,
  getFloraMemories,
  getFloraUserPreferences,
  getOrganizationDocuments,
  getSlashCommands,
  getSuggestedPrompts,
  isAbortError,
  updateFloraUserPreferences,
  updateSlashCommand,
  uploadChatDocument,
};
